import React, { useEffect, useState } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import CustomerApplicationService from 'services/customer-application.service';
import { CustomerApplicationType, CustomerApplication, CustomerApplicationSigner, SignerRepresentative, SignerType, withCreatedApplication } from 'models/customer-application';
import { EMPTY_CUSTOMER_REPRESENTATIVES, SelectableRepresentative, toSelectableRepresentatives } from 'models/customer-representative';
import CustomerService from '../../../services/customer.service';
import { areRepresentativeSignersValid, AuthorizedSignerForm, isAuthorizedSignerValid, SignerSection, toAuthorizedSignerForm, toSelectedRepresentatives, toSignerType } from '../../CustomerCards/SignerSection';
import {InitContextProps, useAuthContext} from '../../../services/context/authentication.context';
import {toast} from 'react-toastify';

interface IncreaseGeneralCreditModalProps {
    handleModalHide: () => void;
    application?: CustomerApplication;
    onSuccess?: () => void;
}

interface IIncreaseGeneralCreditModalForm {
    desiredCredit: string;
    signerType: SignerType;
    selectedSignerRepresentatives: SignerRepresentative[];
    authorizedSigner: AuthorizedSignerForm;
}

export function IncreaseGeneralCreditModal({
    handleModalHide,
    application,
    onSuccess
}: IncreaseGeneralCreditModalProps) {
    const { state, dispatch }: InitContextProps = useAuthContext();
    const { customerId, customer, user } = state;

    const [saving, setSaving] = useState(false);
    const [savingError, setSavingError] = useState(false);
    const [validated, setValidated] = useState(false);

    const [form, setForm] = useState<IIncreaseGeneralCreditModalForm>({
        desiredCredit: application?.creditLimit ?? '',
        signerType: toSignerType(application?.signers),
        selectedSignerRepresentatives: toSelectedRepresentatives(application?.signers),
        authorizedSigner: toAuthorizedSignerForm(application?.signers),
    });

    const storedPowerOfAttorneyFileName = application?.signers
        ?.find(signer => signer.type === SignerType.AUTHORIZED_REPRESENTATIVE)
        ?.powerOfAttorneyFileName;
    const [representatives, setRepresentatives] = useState<SelectableRepresentative[]>([]);
    const [isLoadingRepresentatives, setIsLoadingRepresentatives] = useState(false);

    useEffect(() => {
        if (!customerId) return;

        setIsLoadingRepresentatives(true);
        CustomerService.getRepresentatives(customerId)
            .then(res => setRepresentatives(toSelectableRepresentatives(res ?? EMPTY_CUSTOMER_REPRESENTATIVES)))
            .catch(() => setRepresentatives([]))
            .finally(() => setIsLoadingRepresentatives(false));
    }, [customerId]);

    const isSignerValid = () => {
        if (form.signerType === SignerType.REPRESENTATIVE) {
            return areRepresentativeSignersValid(form.selectedSignerRepresentatives, user?.personalIdentificationCode);
        }

        return isAuthorizedSignerValid(form.authorizedSigner, storedPowerOfAttorneyFileName);
    };

    const buildSigner = (): CustomerApplicationSigner => {
        if (form.signerType === SignerType.REPRESENTATIVE) {
            return { type: SignerType.REPRESENTATIVE, representatives: form.selectedSignerRepresentatives };
        }

        return { type: SignerType.AUTHORIZED_REPRESENTATIVE, ...form.authorizedSigner };
    };

    const handleSend = async () => {
        if (!customerId || !customer || (customer && !customer.contactId)) return;

        setValidated(true);
        if (!isSignerValid()) return;

        setSaving(true);
        setSavingError(false);

        const request = {
            customerId: customerId,
            contactPersonId: customer.contactId,
            customerApplicationType: CustomerApplicationType.INCREASE_CREDIT_APPLICATION,
            creditLimit: form.desiredCredit,
            signer: buildSigner(),
            contactEmail: customer.email ?? '',
            contactPhone: customer?.phone ?? customer?.mobileNumber ?? '',
        };

        try {
            if (isUpdate) {
                await CustomerApplicationService.update(application!.id, request);
            } else {
                const createdApplicationId = await CustomerApplicationService.create(request);

                dispatch({
                    type: "setCustomer",
                    value: {
                        ...state.customer,
                        hasApplications: true,
                        blockingApplications: withCreatedApplication(
                            state.customer.blockingApplications,
                            request.customerApplicationType,
                            createdApplicationId
                        )
                    }
                });
            }

            toast.success("Taotlus edukalt saadetud");
            onSuccess?.();

            setTimeout(() => {
                handleModalHide();
            }, 300);
        } catch (e) {
            if (e === 409) {
                toast.success("Taotlus on juba töötlemisel");
                handleModalHide();
                onSuccess?.();
            } else {
                setSavingError(true);
            }
        } finally{
            setSaving(false);
        }
    };

    const isUpdate = !!application;

    return (
        <Modal 
            show={true} 
            onHide={() => handleModalHide()} 
            backdrop="static"
            keyboard={false}
            centered
            size='lg'
        >
            <Modal.Header closeButton>
                <Modal.Title>
                    {
                        isUpdate ? `Uuenda taotlust #${application!.id}` : 'Suurenda krediiti'
                    }
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form noValidate validated={validated}>
                    <div className="row">
                        <Form.Group controlId="desiredCredit" className="col-md-6 mb-3">
                            <Form.Label>Soovitav krediit</Form.Label>
                            <Form.Control
                                type="number"
                                min="0"
                                value={form.desiredCredit}
                                onChange={e => setForm({ ...form, desiredCredit: e.target.value })}
                                placeholder="Soovitav krediit"
                                disabled={saving}
                            />
                        </Form.Group>
                    </div>
                    <div className="row">
                        <SignerSection
                            signerType={form.signerType}
                            onSignerTypeChange={signerType => setForm({ ...form, signerType })}
                            representatives={representatives}
                            isLoadingRepresentatives={isLoadingRepresentatives}
                            selectedRepresentatives={form.selectedSignerRepresentatives}
                            onSelectedRepresentativesChange={selectedSignerRepresentatives =>
                                setForm({ ...form, selectedSignerRepresentatives })
                            }
                            authorizedSigner={form.authorizedSigner}
                            onAuthorizedSignerChange={authorizedSigner => setForm({ ...form, authorizedSigner })}
                            currentUserPersonalCode={user?.personalIdentificationCode}
                            storedPowerOfAttorneyFileName={storedPowerOfAttorneyFileName}
                            applicationId={application?.id}
                            customerId={customerId ?? undefined}
                            disabled={saving}
                            validated={validated}
                        />
                    </div>
                </Form>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={() => handleModalHide()} disabled={saving}>
                    {
                        isUpdate ? 'Tühista' : 'Loobu'
                    }
                </Button>
                <Button
                    variant="primary"
                    onClick={handleSend}
                    disabled={saving || !form.desiredCredit}
                >
                    {
                        isUpdate ? 'Salvesta' : 'Saada taotlus'
                    }
                </Button>
            </Modal.Footer>
            {
                !saving && savingError &&
              <span className="d-flex justify-content-end text-danger px-2 pb-2">Viga salvestamisel, proovi mõne hetke pärast uuesti.</span>
            }
        </Modal>
    );
}
